import React, { useCallback, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const SWIPE_THRESHOLD = 50;

function padIndex(n, total) {
  return ((n % total) + total) % total;
}

function cardStyle(offset, reducedMotion) {
  if (reducedMotion) {
    return {
      opacity: offset === 0 ? 1 : 0,
      transform: 'translate3d(0,0,0) scale(1)',
      zIndex: offset === 0 ? 30 : 0,
      pointerEvents: offset === 0 ? 'auto' : 'none',
    };
  }
  const abs = Math.abs(offset);
  if (abs > 1) {
    return {
      opacity: 0,
      transform: 'translate3d(0,0,-120px) scale(0.7)',
      zIndex: 0,
      pointerEvents: 'none',
    };
  }
  const scale = offset === 0 ? 1 : 0.92;
  const opacity = offset === 0 ? 1 : 0.4;
  const z = offset === 0 ? 0 : -abs * 40;
  return {
    opacity,
    transform: `translate3d(0, 0, ${z}px) scale(${scale})`,
    zIndex: 30 - abs * 10,
    pointerEvents: offset === 0 ? 'auto' : 'none',
  };
}

const StackedCardCarousel = ({ slides }) => {
  const total = slides.length;
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const dragStart = useRef(null);
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  const goTo = useCallback(
    (next) => setIndex(padIndex(next, total)),
    [total]
  );

  const onPointerDown = (e) => {
    dragStart.current = e.clientX;
    setDragX(0);
  };

  const onPointerMove = (e) => {
    if (dragStart.current == null) return;
    setDragX(e.clientX - dragStart.current);
  };

  const onPointerUp = () => {
    if (dragStart.current == null) return;
    if (dragX > SWIPE_THRESHOLD) goTo(index - 1);
    else if (dragX < -SWIPE_THRESHOLD) goTo(index + 1);
    dragStart.current = null;
    setDragX(0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') goTo(index - 1);
    if (e.key === 'ArrowRight') goTo(index + 1);
  };

  return (
    <div className="w-full select-none">
      <div className="relative flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => goTo(index - 1)}
          className="shrink-0 rounded-full border border-line p-2 text-ink hover:border-accent transition-colors"
        >
          <FiChevronLeft className="h-5 w-5" />
        </button>

        <div
          role="region"
          aria-label="Achievements carousel"
          tabIndex={0}
          data-testid="carousel-stage"
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          className="relative flex-1 h-[min(65vh,420px)] sm:h-[min(75vh,560px)] outline-none touch-pan-y overflow-hidden rounded-lg isolate"
          style={{ perspective: '1200px' }}
        >
          <div
            className="relative w-full h-full"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {slides.map((slide, i) => {
              let offset = i - index;
              if (offset > total / 2) offset -= total;
              if (offset < -total / 2) offset += total;
              const style = cardStyle(offset, reducedMotion);
              const dragOffset = offset === 0 ? dragX : 0;
              return (
                <div
                  key={slide.alt}
                  className={`absolute inset-0 flex items-center justify-center ${
                    reducedMotion ? '' : 'duration-400 ease-out transition-all'
                  }`}
                  style={{
                    ...style,
                    transform: reducedMotion
                      ? style.transform
                      : `${style.transform} translateX(${dragOffset}px)`,
                    transitionTimingFunction: reducedMotion
                      ? undefined
                      : 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="max-h-full max-w-full rounded-lg object-contain shadow-xl"
                    draggable={false}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          aria-label="Next slide"
          onClick={() => goTo(index + 1)}
          className="shrink-0 rounded-full border border-line p-2 text-ink hover:border-accent transition-colors"
        >
          <FiChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-4 flex justify-center gap-2 flex-wrap">
        {slides.map((slide, i) => (
          <button
            key={slide.alt}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === index ? 'true' : undefined}
            onClick={() => goTo(i)}
            className={`rounded-md border-2 overflow-hidden transition-colors ${
              i === index ? 'border-accent' : 'border-line opacity-70 hover:opacity-100'
            }`}
          >
            <img
              src={slide.src}
              alt=""
              className="h-14 w-20 object-cover"
              draggable={false}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default StackedCardCarousel;
